<script>
import { onDestroy } from "svelte";
import { toast } from "@zerodevx/svelte-toast";
import { showModal } from "@app/store/modalStore.svelte.js";
import {
    clearLiveCaptions,
    disableLiveCaptionSession,
    enableLiveCaptionSession,
    liveCaptionSession,
    liveCaptionState,
    saveLiveCaptionSettings,
    sendLiveCaptionLine,
} from "@app/store/liveCaptionStore.svelte.js";
import { getPlatformConfig, PLATFORM_IDS } from "@app/lib/liveCaption/index.js";
import * as m from "@app/paraglide/messages.js";

let currentLine = $state("");
let elapsedMs = $state(0);
let isBusy = $state(false);
let textareaEl = $state();
let cursorIndex = $state(0);

let platformConfig = $derived(getPlatformConfig(liveCaptionState.platform));
let currentPhysicalLine = $derived(getPhysicalLineAtCursor(currentLine, cursorIndex));
let charRemaining = $derived(Number(liveCaptionState.maxChars) - currentPhysicalLine.length);
let showPasteToken = $derived(
    liveCaptionState.platform === PLATFORM_IDS.ZOOM || liveCaptionState.platform === PLATFORM_IDS.TEAMS,
);

const timer = setInterval(() => {
    elapsedMs = liveCaptionSession.sessionStartMs ? Date.now() - liveCaptionSession.sessionStartMs : 0;
}, 500);

onDestroy(() => {
    clearInterval(timer);
});

function formatElapsed(ms) {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600).toString().padStart(2, "0");
    const minutes = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, "0");
    const seconds = Math.floor(totalSeconds % 60).toString().padStart(2, "0");

    return `${hours}:${minutes}:${seconds}`;
}

function statusLabel(status) {
    switch (status) {
        case "connecting":
            return m.live_caption_status_connecting();
        case "connected":
            return m.live_caption_status_connected();
        case "sending":
            return m.live_caption_status_sending();
        case "error":
            return m.live_caption_status_error();
        default:
            return m.live_caption_status_idle();
    }
}

async function handleEnableChange() {
    isBusy = true;

    try {
        if (liveCaptionSession.enabled) {
            await disableLiveCaptionSession();
            toast.push(m.live_caption_disabled_toast(), { classes: ["toast-info"] });
        } else {
            await enableLiveCaptionSession();
            textareaEl?.focus();
            toast.push(m.live_caption_enabled_toast(), { classes: ["toast-success"] });
        }
    } catch (err) {
        liveCaptionSession.connectionStatus = "error";
        liveCaptionSession.lastError = err.message;
        toast.push(err.message, { classes: ["toast-danger"] });
    } finally {
        isBusy = false;
    }
}

async function sendCurrentLine() {
    if (isBusy) return;

    isBusy = true;
    const lineToSend = currentLine;

    try {
        await sendLiveCaptionLine(lineToSend);
        currentLine = "";
        cursorIndex = 0;
    } catch (err) {
        toast.push(err.message, { classes: ["toast-danger"] });
        if (lineToSend.trim()) {
            currentLine = "";
            cursorIndex = 0;
        }
    } finally {
        isBusy = false;
        setTimeout(() => {
            textareaEl?.focus();
        }, 100);
    }
}

async function handleClear() {
    if (isBusy) return;

    isBusy = true;
    try {
        await clearLiveCaptions();
        currentLine = "";
        cursorIndex = 0;
    } catch (err) {
        toast.push(err.message, { classes: ["toast-danger"] });
    } finally {
        isBusy = false;
        textareaEl?.focus();
    }
}

function handleKeydown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendCurrentLine();
    }
}

function getPhysicalLineAtCursor(value, caretIndex) {
    const safeCaretIndex = Math.min(Math.max(caretIndex, 0), value.length);
    const lineStart = value.lastIndexOf("\n", safeCaretIndex - 1) + 1;
    const nextLineBreak = value.indexOf("\n", safeCaretIndex);
    const lineEnd = nextLineBreak === -1 ? value.length : nextLineBreak;

    return value.slice(lineStart, lineEnd);
}

function wrapPhysicalLine(value, caretIndex, maxChars) {
    const safeMaxChars = Number(maxChars);

    if (!Number.isFinite(safeMaxChars) || safeMaxChars <= 0) {
        return { value, cursorIndex: caretIndex };
    }

    let wrappedValue = value;
    let wrappedCursorIndex = Math.min(Math.max(caretIndex, 0), wrappedValue.length);

    while (getPhysicalLineAtCursor(wrappedValue, wrappedCursorIndex).length > safeMaxChars) {
        const lineStart = wrappedValue.lastIndexOf("\n", wrappedCursorIndex - 1) + 1;
        const nextLineBreak = wrappedValue.indexOf("\n", wrappedCursorIndex);
        const lineEnd = nextLineBreak === -1 ? wrappedValue.length : nextLineBreak;
        const line = wrappedValue.slice(lineStart, lineEnd);
        let splitIndex = -1;

        for (let i = Math.min(safeMaxChars, line.length - 1); i > 0; i -= 1) {
            if (/\s/.test(line[i])) {
                splitIndex = i;
                break;
            }
        }

        if (splitIndex === -1) {
            splitIndex = safeMaxChars;
            wrappedValue = `${wrappedValue.slice(0, lineStart + splitIndex)}\n${wrappedValue.slice(lineStart + splitIndex)}`;

            if (wrappedCursorIndex > lineStart + splitIndex) {
                wrappedCursorIndex += 1;
            }
        } else {
            wrappedValue = `${wrappedValue.slice(0, lineStart + splitIndex)}\n${wrappedValue.slice(lineStart + splitIndex + 1)}`;

            if (wrappedCursorIndex === lineStart + splitIndex) {
                wrappedCursorIndex += 1;
            }
        }
    }

    return { value: wrappedValue, cursorIndex: wrappedCursorIndex };
}

function handleSelectionChange(e) {
    cursorIndex = e.target.selectionStart ?? currentLine.length;
}

function handleInput(e) {
    const target = e.target;
    let nextLine = target.value;
    let nextCursorIndex = target.selectionStart ?? nextLine.length;

    if (liveCaptionState.autoWrap && liveCaptionSession.enabled) {
        const wrapped = wrapPhysicalLine(nextLine, nextCursorIndex, liveCaptionState.maxChars);
        nextLine = wrapped.value;
        nextCursorIndex = wrapped.cursorIndex;
        target.value = nextLine;
        target.setSelectionRange(nextCursorIndex, nextCursorIndex);
    }

    currentLine = nextLine;
    cursorIndex = nextCursorIndex;
}

async function pasteNewToken() {
    try {
        liveCaptionState.endpoint = await navigator.clipboard.readText();
        saveLiveCaptionSettings();
        toast.push(m.live_caption_token_pasted_toast(), { classes: ["toast-success"] });
    } catch (err) {
        toast.push(err.message, { classes: ["toast-danger"] });
    }
}
</script>

<section class="live-captioning-tool" aria-label={m.live_caption_title()}>
    <div class="d-flex justify-content-between align-items-start gap-2 mb-2">
        <div>
            <div class="d-flex align-items-center gap-2 small text-muted">
                <span class="status-dot status-{liveCaptionSession.connectionStatus}" aria-hidden="true"></span>
                <span>{statusLabel(liveCaptionSession.connectionStatus)}</span>
                {#if liveCaptionState.showSessionTimer}
                    <span aria-hidden="true">|</span>
                    <span>{formatElapsed(elapsedMs)}</span>
                {/if}
            </div>
        </div>
        <div class="d-flex align-items-center gap-2">
            <div class="form-check form-switch mb-0">
                <input
                    class="form-check-input"
                    type="checkbox"
                    id="liveCaptionEnableSwitch"
                    checked={liveCaptionSession.enabled}
                    disabled={isBusy}
                    onchange={handleEnableChange}
                    aria-labelledby="liveCaptionEnableLabel"
                />
                <label class="form-check-label small" id="liveCaptionEnableLabel" for="liveCaptionEnableSwitch">
                    {m.live_caption_enable()}
                </label>
            </div>
            <button
                class="btn btn-sm btn-outline-secondary"
                type="button"
                onclick={() => showModal("liveCaptionSettings")}
                aria-label={m.live_caption_open_settings()}>
                <i class="bi bi-gear" aria-hidden="true"></i>
            </button>
        </div>
    </div>

    <div class="d-flex justify-content-between align-items-center gap-2 mb-2">
        <div class="small text-muted text-truncate">
            {platformConfig.label}: {liveCaptionState.endpoint || m.live_caption_no_endpoint()}
        </div>
        {#if showPasteToken}
            <button class="btn btn-sm btn-outline-primary flex-shrink-0" type="button" onclick={pasteNewToken}>
                <i class="bi bi-clipboard" aria-hidden="true"></i> {m.live_caption_paste_new_token()}
            </button>
        {/if}
    </div>

    {#if liveCaptionSession.lastError}
        <div class="alert alert-danger py-2 small" role="alert">
            {liveCaptionSession.lastError}
        </div>
    {/if}

    <div class="mb-2">
        <label class="form-label small mb-1" for="liveCaptionInput">{m.live_caption_current_line()}</label>
        <textarea
            id="liveCaptionInput"
            class="form-control"
            rows="2"
            bind:this={textareaEl}
            value={currentLine}
            oninput={handleInput}
            onkeydown={handleKeydown}
            onkeyup={handleSelectionChange}
            onclick={handleSelectionChange}
            onselect={handleSelectionChange}
            disabled={!liveCaptionSession.enabled}
            style:font-size={`${liveCaptionState.fontSize}px`}
            placeholder={m.live_caption_input_placeholder()}></textarea>
        {#if liveCaptionState.showCharCounter}
            <div class="form-text {charRemaining < 0 ? 'text-danger' : ''}">
                {m.live_caption_chars_remaining({ count: charRemaining })}
            </div>
        {/if}
    </div>

    <div class="d-flex justify-content-end gap-2">
        <button
            class="btn btn-primary btn-sm"
            type="button"
            disabled={!liveCaptionSession.enabled || isBusy}
            onclick={sendCurrentLine}>
            {m.live_caption_send()}
        </button>
        <button
            class="btn btn-outline-secondary btn-sm"
            type="button"
            disabled={!liveCaptionSession.enabled || isBusy}
            onclick={handleClear}>
            {m.live_caption_clear()}
        </button>
    </div>
</section>

<style>
    .live-captioning-tool {
        min-height: 260px;
    }

    .status-dot {
        display: inline-block;
        width: 0.65rem;
        height: 0.65rem;
        border-radius: 50%;
        background: var(--bs-secondary);
    }

    .status-connected {
        background: var(--bs-success);
    }

    .status-connecting,
    .status-sending {
        background: var(--bs-warning);
    }

    .status-error {
        background: var(--bs-danger);
    }

</style>
